<template>
  <div class="chatbot-root">
    <transition name="slide-up">
      <div v-if="isOpen" class="chat-window" :class="[themeClasses, { 'minimized': isMinimized }]" :style="windowStyle" ref="chatWindow">
        <header class="chat-header" @mousedown="startWindowDrag">
          <div class="header-content">
            <div class="bot-avatar">
              <Bot class="bot-icon" />
              <div class="status-indicator"></div>
            </div>
            <div v-if="!isMinimized" class="header-info">
              <h3 class="header-title">{{ title }}</h3>
              <p class="header-subtitle">{{ isTyping ? 'Typing...' : 'Online • Responds instantly' }}</p>
            </div>
          </div>
          <div class="header-actions">
            <button @click="toggleMinimize" class="action-btn" :aria-label="isMinimized ? 'Maximize chat' : 'Minimize chat'">
              <span class="minus-icon"></span>
            </button>
            <button @click="toggleExpand" class="action-btn" aria-label="Expand chat" v-if="!isMinimized">
              <Maximize2 class="action-icon" />
            </button>
            <button @click="toggleDarkMode" class="action-btn" title="Toggle theme">
              <Sun v-if="!isDarkMode" class="action-icon" />
              <Moon v-else class="action-icon" />
            </button>
            <button @click="closeChat" class="action-btn close-btn" aria-label="Close chat">
              <X class="action-icon" />
            </button>
          </div>
        </header>

        <div v-if="!isMinimized" class="chat-body-wrapper">
          <div class="chat-body">
            <div v-if="messages.length === 0" class="welcome-screen">
              <div class="welcome-content">
                <h4 class="welcome-title">Welcome to OrderCircle AI Assistant!</h4>
                <p class="welcome-subtitle">
                  I'm here to help you with any questions about features, setup, or troubleshooting.
                </p>
                <div v-if="!azureAvailable" class="azure-status-warning">
                  <p><strong>Note:</strong> Azure OpenAI service is currently not available.</p>
                  <p>Please check your configuration or contact support.</p>
                </div>
                <div class="quick-actions">
                  <button
                    v-for="action in quickActions"
                    :key="action.id"
                    @click="sendQuickMessage(action.message)"
                    class="quick-action-btn"
                  >
                    <component :is="action.icon" class="quick-action-icon" />
                    <span>{{ action.label }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="messages-container" ref="messagesContainer">
              <transition-group name="message" tag="div" class="messages-list">
                <div
                  v-for="message in displayedMessages"
                  :key="message.id"
                  class="message-wrapper"
                  :class="{ 'user-message': message.sender === 'user', 'bot-message': message.sender === 'assistant' }"
                >
                  <div class="message-content">
                    <div class="message-bubble" :class="message.sender">
                      <div v-if="message.sender === 'user'" class="user-message-content">
                        {{ message.content }}
                      </div>
                      <div v-else class="bot-message-content" v-html="getDisplayContent(message)"></div>
                      <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                    </div>
                  </div>
                </div>
              </transition-group>
              <div v-if="isTyping" class="typing-indicator">
                <div class="typing-bubble">
                  <div class="typing-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer class="chat-input-area">
            <div class="input-container">
              <textarea
                ref="messageInput"
                v-model="currentMessage"
                @keydown="handleKeyDown"
                @input="handleInput"
                placeholder="Type your message..."
                :disabled="isLoading"
                class="message-input"
                rows="1"
                maxlength="2000"
              ></textarea>
              <button @click="sendMessage" :disabled="!currentMessage.trim() || isLoading" class="send-btn" aria-label="Send message">
                <Send v-if="!isLoading" class="send-icon" />
                <div v-else class="loading-spinner"></div>
              </button>
            </div>
            <div class="input-actions">
              <button @click="clearChat" class="clear-btn" title="Clear chat">
                <RefreshCw class="clear-icon" />
                <span>Clear</span>
              </button>
            </div>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  Bot, Send, X, Maximize2, Sun, Moon, RefreshCw,
  HelpCircle, Settings, BookOpen, Phone
} from 'lucide-vue-next';
import DOMPurify from 'dompurify';
import { 
  getChatbotResponse, 
  formatMessageContent, 
  checkAzureStatus
} from './ChatbotService';

import './ChatbotWidget.css';

export default {
  name: 'ChatbotWidget',
  components: {
    Bot, Send, X, Maximize2, Sun, Moon, RefreshCw,
    HelpCircle, Settings, BookOpen, Phone
  },
  props: {
    title: { type: String, default: 'OrderCircle AI Assistant' },
    initialOpen: { type: Boolean, default: false },
    defaultDarkMode: { type: Boolean, default: false }
  },
  data() {
    return {
      isOpen: this.initialOpen,
      isMinimized: false,
      isDarkMode: this.defaultDarkMode,
      currentMessage: '',
      messages: [],
      isLoading: false,
      isTyping: false,
      chatWidth: 400,
      chatHeight: 600,
      isDraggingWindow: false,
      windowTop: 'auto',
      windowBottom: '100px',
      windowRight: '24px',
      windowLeft: 'auto',
      dragStartX: 0,
      dragStartY: 0,
      azureAvailable: false,
      saveTimeout: null, // For debouncing saves
      quickActions: [
         { id: 1, label: 'Getting Started', message: 'How do I get started?', icon: 'BookOpen' },
         { id: 2, label: 'Account Setup', message: 'Help me set up my account', icon: 'Settings' },
         { id: 3, label: 'Features Guide', message: 'What features are available?', icon: 'HelpCircle' },
         { id: 4, label: 'Contact Support', message: 'How do I contact support?', icon: 'Phone' }
       ]
    };
  },
  computed: {
    themeClasses() {
      return {
        'dark-theme': this.isDarkMode,
        'light-theme': !this.isDarkMode
      };
    },
    windowStyle() {
      return {
        width: `${this.chatWidth}px`,
        height: this.isMinimized ? '64px' : `${this.chatHeight}px`,
        top: this.windowTop !== 'auto' ? this.windowTop : 'auto',
        bottom: this.windowBottom !== 'auto' ? this.windowBottom : 'auto',
        left: this.windowLeft !== 'auto' ? this.windowLeft : 'auto',
        right: this.windowRight !== 'auto' ? this.windowRight : 'auto',
      };
    },
    // Optimize message rendering by limiting displayed messages
    displayedMessages() {
      // Only show last 50 messages for better performance
      return this.messages.slice(-50);
    }
  },
  mounted() {
    this.loadChatHistory();
    this.loadChatSettings();
    this.loadWindowPosition();
    this.setupEventListeners();
    // Remove Azure status check on mount - it causes UI delays
    // Only check when actually needed (when sending messages)
    this.azureAvailable = true; // Assume available by default for faster UI
  },
  beforeUnmount() {
    this.cleanupEventListeners();
    // Cleanup save timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  },
  methods: {
    openChat() {
      this.isOpen = true;
      this.isMinimized = false;
      this.focusInput();
    },
    closeChat() {
      this.isOpen = false;
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      if (!this.isMinimized) this.focusInput();
    },
    toggleExpand() {
      const isExpanded = this.chatWidth > 400 || this.chatHeight > 600;
      this.chatWidth = isExpanded ? 400 : Math.min(window.innerWidth - 48, 800);
      this.chatHeight = isExpanded ? 600 : Math.min(window.innerHeight - 48, 1000);
      this.saveChatSettings();
      this.scrollToBottom();
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      this.saveChatSettings();
    },
    async sendMessage() {
      if (!this.currentMessage.trim() || this.isLoading) return;
      
      const userMessage = {
        id: Date.now(),
        sender: 'user',
        content: this.currentMessage.trim(),
        timestamp: new Date()
      };

      this.messages.push(userMessage);
      const messageText = this.currentMessage;
      this.currentMessage = '';
      this.resizeTextarea();

      this.scrollToBottom();
      this.saveChatHistory();

      this.isTyping = true;
      this.isLoading = true;

      try {
        // Lazy check Azure status only when sending first message
        if (this.messages.length === 1) {
          this.azureAvailable = await this.checkAzureStatus();
        }
        
        if (!this.azureAvailable) {
          const botMessage = {
            id: Date.now() + 1,
            sender: 'assistant',
            content: 'I apologize, but the AI service is currently unavailable. Please try again in a 60 seconds or contact support at support@ordercircle.com.',
            timestamp: new Date()
          };
          this.messages.push(botMessage);
        } else {
          const botResponse = await getChatbotResponse(messageText, this.messages);
          
          const botMessage = {
            id: Date.now() + 1,
            sender: 'assistant',
            content: botResponse,
            timestamp: new Date()
          };
          this.messages.push(botMessage);
        }
      } catch (error) {
        console.error('Chatbot error:', error);
        const errorMessage = {
          id: Date.now() + 1,
          sender: 'assistant',
          content: 'Sorry, I encountered an error. Please try again or contact support at support@ordercircle.com.',
          timestamp: new Date()
        };
        this.messages.push(errorMessage);
      } finally {
        this.isTyping = false;
        this.isLoading = false;
        this.scrollToBottom();
        this.saveChatHistory();
        this.focusInput();
      }
    },
    sendQuickMessage(message) {
      this.currentMessage = message;
      this.sendMessage();
    },
    clearChat() {
      this.messages = [];
      this.saveChatHistory();
      this.focusInput();
    },
    handleKeyDown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    handleInput() {
      this.resizeTextarea();
    },
    resizeTextarea() {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        const textarea = this.$refs.messageInput;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
        }
      });
    },
    focusInput() {
      // Only focus if not minimized and input exists
      if (!this.isMinimized && this.$refs.messageInput) {
        this.$refs.messageInput.focus();
      }
    },
    scrollToBottom() {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    getDisplayContent(message) {
      if (!message || !message.content) return '<p>...</p>';
      const formatted = formatMessageContent(message.content, this.isDarkMode);
      return DOMPurify.sanitize(formatted, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'code', 'pre', 'div', 'span', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3'],
        ALLOWED_ATTR: ['class', 'href', 'target']
      });
    },
    startWindowDrag(event) {
      if (event.button !== 0 || event.target.closest('.action-btn')) return;
      this.isDraggingWindow = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      if (this.$refs.chatWindow) {
        this.$refs.chatWindow.style.transition = 'none';
      }
      document.addEventListener('mousemove', this.handleWindowDrag);
      document.addEventListener('mouseup', this.stopWindowDrag);
    },
    handleWindowDrag(event) {
      if (!this.isDraggingWindow) return;
      event.preventDefault();
      
      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;
      
      const rect = this.$refs.chatWindow.getBoundingClientRect();
      let newLeft = rect.left + dx;
      let newTop = rect.top + dy;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      
      const padding = 24;
      newLeft = Math.max(padding, Math.min(newLeft, window.innerWidth - this.chatWidth - padding));
      newTop = Math.max(padding, Math.min(newTop, window.innerHeight - (this.isMinimized ? 64 : this.chatHeight) - padding));
      
      // Use direct positioning for reliable dragging
      this.windowTop = `${newTop}px`;
      this.windowLeft = `${newLeft}px`;
      this.windowBottom = 'auto';
      this.windowRight = 'auto';
    },
    stopWindowDrag() {
      if (!this.isDraggingWindow) return;
      this.isDraggingWindow = false;
      
      if (this.$refs.chatWindow) {
        this.$refs.chatWindow.style.transition = '';
      }
      
      document.removeEventListener('mousemove', this.handleWindowDrag);
      document.removeEventListener('mouseup', this.stopWindowDrag);
      this.saveWindowPosition();
    },
    saveWindowPosition() {
      const rect = this.$refs.chatWindow.getBoundingClientRect();
      const pos = {
        top: `${rect.top}px`,
        bottom: 'auto',
        left: `${rect.left}px`,
        right: 'auto'
      };
      localStorage.setItem('chatbot_window_pos', JSON.stringify(pos));
    },
    loadWindowPosition() {
      const pos = localStorage.getItem('chatbot_window_pos');
      if (pos) {
        try {
          const parsedPos = JSON.parse(pos);
          this.windowTop = parsedPos.top;
          this.windowBottom = parsedPos.bottom;
          this.windowLeft = parsedPos.left;
          this.windowRight = parsedPos.right;
        } catch (e) {
          console.warn('Could not load window position:', e);
        }
      }
    },
    setupEventListeners() {
      window.addEventListener('resize', this.adjustChatSize);
    },
    cleanupEventListeners() {
      window.removeEventListener('resize', this.adjustChatSize);
      document.removeEventListener('mousemove', this.handleWindowDrag);
      document.removeEventListener('mouseup', this.stopWindowDrag);
    },
    adjustChatSize() {
      this.chatWidth = Math.min(this.chatWidth, window.innerWidth - 48);
      this.chatHeight = Math.min(this.chatHeight, window.innerHeight - 48);
    },
    saveChatHistory() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        try {
          localStorage.setItem('chatbot_history', JSON.stringify(this.messages.slice(-50)));
        } catch (e) { 
          console.warn("Could not save chat history:", e); 
        }
      }, 500); // 500ms debounce time
    },
    loadChatHistory() {
      try {
        const history = localStorage.getItem('chatbot_history');
        if (history) {
          this.messages = JSON.parse(history);
        }
      } catch (e) { 
        console.warn("Could not load chat history:", e); 
        this.messages = []; 
      }
    },
    saveChatSettings() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        try {
          const settings = {
            isDarkMode: this.isDarkMode,
            chatWidth: this.chatWidth,
            chatHeight: this.chatHeight,
          };
          localStorage.setItem('chatbot_settings', JSON.stringify(settings));
        } catch (e) { 
          console.warn("Could not save settings:", e); 
        }
      }, 500); // 500ms debounce time
    },
    loadChatSettings() {
      try {
        const settings = localStorage.getItem('chatbot_settings');
        if (settings) {
          const parsedSettings = JSON.parse(settings);
          this.isDarkMode = parsedSettings.isDarkMode ?? this.defaultDarkMode;
          this.chatWidth = parsedSettings.chatWidth ?? 400;
          this.chatHeight = parsedSettings.chatHeight ?? 600;
        }
      } catch (e) { 
        console.warn("Could not load settings:", e); 
      }
    },
    async checkAzureStatus() {
      const isOnline = await checkAzureStatus();
      if (!isOnline) {
        console.warn('Azure OpenAI is not available');
      }
      return isOnline;
    }
  }
};
</script>
